/* ============================================================
   le-quick-add.js — Lab Effects shared quick-add slide-up.

   A page-agnostic mini-buybox that mirrors the PDP hero: a category-
   coloured product stage, acronym + eyebrow + title, aroma chip,
   one chip row per non-size option (Plant Source, Flavor Enhancements,
   …) that re-prices the size cards, a quantity stepper, and an Add
   button that shows the live total. Used by the collection cards and
   the PDP related-products carousel — anywhere an ATC octagon opens
   the sheet.

   Trigger contract: any element with [data-quick-add] and a [data-qa]
   JSON payload (see snippets/le-quick-add-json.liquid). Variant prices
   / availability are fetched from /products/<handle>.js on open, so
   cards stay light even for 78-variant products.

   Singleton markup: snippets/le-quick-add-sheet.liquid (#le-qa-sheet).
   Uses event delegation, so it survives grid re-renders (collection
   switch, related-products AJAX) with no re-binding.
   ============================================================ */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---- Add to cart → native Horizon cart drawer (reuse PDP helper) ---- */
  /* ONE add-to-cart path, and this is not it — see CLAUDE.md.

     This function used to carry a full second copy of the /cart/add.js logic
     as a fallback "if window.LE_addToCart is missing". That fallback was not a
     safety net, it was the code that actually ran everywhere except a product
     page: le-cart-add.js was emitted only by the two PDP sections, so on a
     collection page, the homepage, or anywhere else a product card appears,
     the helper did not exist and the copy took over.

     It had drifted from the real one in exactly the ways a shopper notices. It
     never dispatched 'le:cart-added', so the confirmation toast never dropped
     on the surfaces where quick-add is most used. And it called
     openCartDrawer() unconditionally, where the real helper suppresses the
     drawer below 768px — so on mobile Horizon pinned body{position:fixed} for a
     drawer living inside the hidden navbar, the page snapped to the top, and
     le-mobile.js's watchdog then scrolled it back down. That was the bounce
     when the sheet closed.

     le-cart-add.js is now loaded globally from layout/theme.liquid, ahead of
     this file and with both marked `defer`, so LE_addToCart is always defined
     by the time anything here can call it. If it is somehow missing, fail
     loudly rather than silently re-growing a second implementation. */
  function addToCart(variantId, qty) {
    if (!window.LE_addToCart) {
      return Promise.reject(new Error(
        'LE_addToCart missing — le-cart-add.js must load before le-quick-add.js'
      ));
    }
    return window.LE_addToCart(variantId, qty);
  }

  /* ---- Product fetch (/products/<handle>.js) with cache ---- */
  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    var pr = fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('product fetch failed'); return r.json(); })
      .then(function (pj) {
        var optionNames = (pj.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
        var variants = (pj.variants || []).map(function (v) {
          var opts = {};
          (v.options || []).forEach(function (val, i) { opts[optionNames[i]] = val; });
          return { id: v.id, price: v.price, compareAt: v.compare_at_price || 0, available: v.available, opts: opts };
        });
        return { optionNames: optionNames, variants: variants };
      });
    pr.catch(function () { delete productCache[handle]; }); // allow retry on failure
    productCache[handle] = pr;
    return pr;
  }

  function init() {
    var sheet = document.getElementById('le-qa-sheet');
    var scrim = document.getElementById('le-qa-scrim');
    if (!sheet) return;

    var el = {
      stage: sheet.querySelector('[data-qa-stage]'),
      img: sheet.querySelector('[data-qa-img]'),
      abbr: sheet.querySelector('[data-qa-abbr]'),
      cat: sheet.querySelector('[data-qa-cat]'),
      title: sheet.querySelector('[data-qa-title]'),
      aromaWrap: sheet.querySelector('[data-qa-aroma-wrap]'),
      aroma: sheet.querySelector('[data-qa-aroma]'),
      view: sheet.querySelector('[data-qa-view]'),
      note: sheet.querySelector('[data-qa-note]'),
      plant: sheet.querySelector('[data-qa-plant]'),
      groups: sheet.querySelector('[data-qa-groups]'),
      sampleWrap: sheet.querySelector('[data-qa-sample]'),
      sampleLabel: sheet.querySelector('[data-qa-sample-label]'),
      sampleMount: sheet.querySelector('[data-qa-sample-mount]'),
      sizeSection: sheet.querySelector('[data-qa-size-section]'),
      sizeLabel: sheet.querySelector('[data-qa-size-label]'),
      sizes: sheet.querySelector('[data-qa-sizes]'),
      qtyVal: sheet.querySelector('[data-qa-qty-val]'),
      qtyMinus: sheet.querySelector('[data-qa-qty-minus]'),
      qtyPlus: sheet.querySelector('[data-qa-qty-plus]'),
      atc: sheet.querySelector('[data-qa-atc]'),
      atcPrice: sheet.querySelector('[data-qa-atc-price]')
    };

    var state = null;       // { data, product, sel, qty, sizeOption }
    var closeTimeout = null;

    /* Repaint the category-dependent chrome. `c` is a per-source category from
       the payload, or undefined to fall back to the product's own. */
    function paintCategory(c) {
      if (!state) return;
      var d = state.data || {};
      var abbr  = (c && c.abbr)  || d.abbr || '';
      var label = (c && c.label) || d.catLabel || '';
      var text  = (c && c.text)  || d.catText || '#108090';
      var tagBg = (c && c.tagBg) || d.catTagBg || 'rgba(16,128,144,.16)';
      var soft  = (c && c.soft)  || d.catSoft || '#eef1ea';
      var img   = (c && c.img)   || d.img || '';
      if (el.abbr) { el.abbr.textContent = abbr; el.abbr.style.background = tagBg; el.abbr.style.color = text; }
      if (el.cat)   el.cat.textContent = label;
      if (el.title) el.title.style.color = text;
      if (el.stage) el.stage.style.background = soft;
      if (el.img && img) { el.img.src = img; el.img.style.visibility = 'visible'; }
    }

    /* The `cat` block attached to the currently-selected plant value, if any. */
    function selectedPlantCat() {
      if (!state || !state.data) return null;
      var groups = state.data.chipGroups || [];
      for (var i = 0; i < groups.length; i++) {
        var g = groups[i];
        if (!g.plant) continue;
        var chosen = state.sel[g.name];
        var v = (g.values || []).find(function (x) { return x.v === chosen; });
        if (v && v.cat) return v.cat;
      }
      return null;
    }

    /* ---------- open / close ---------- */
    function openSheet(data) {
      if (closeTimeout) { clearTimeout(closeTimeout); closeTimeout = null; }
      state = { data: data, product: null, sel: {}, qty: 1, sizeOption: data.sizeOption || '', isSample: !!data.isSample, sampleApi: null, plantDefault: data.plantDefault || '' };

      /* THE HEADER FOLLOWS THE SELECTED SOURCE.

         On a merged (tri-source) product each Plant Source value IS its own
         category — CDT green, CBS red, BDT orange — with its own name, acronym
         and bottle. The payload carries that per value as `cat`; paintCategory
         applies it. It runs on open (so the sheet lands on the right branding
         for whatever source got seeded) and again on every plant chip tap.

         Before this, the header was painted once from the PRODUCT's category
         and never again: picking Botanical Derived left a green CDT tag, a
         green title and the CDT bottle sitting above an orange chip.

         Anything without a per-variant category emits no `cat` and falls back
         to the product's own values, so nothing else changes. */
      // Header
      if (el.stage) el.stage.style.background = data.catSoft || '#eef1ea';
      if (el.img) {
        if (data.img) { el.img.src = data.img; el.img.alt = data.title || 'Product image'; el.img.style.visibility = 'visible'; }
        else { el.img.removeAttribute('src'); el.img.style.visibility = 'hidden'; }
      }
      if (el.abbr) {
        el.abbr.textContent = data.abbr || '';
        el.abbr.style.background = data.catTagBg || 'rgba(16,128,144,.16)';
        el.abbr.style.color = data.catText || '#108090';
      }
      if (el.cat) el.cat.textContent = data.catLabel || '';
      if (el.title) { el.title.textContent = data.title || ''; el.title.style.color = data.catText || '#108090'; }
      if (el.view) { el.view.href = data.url || ('/products/' + (data.handle || '')); }
      if (el.aromaWrap && el.aroma) {
        if (data.aroma) {
          el.aroma.textContent = (data.aroma || '').toUpperCase();
          el.aroma.style.background = data.aromaColor || '#125D67';
          el.aroma.style.color = data.aromaText || '#fff';
          el.aromaWrap.hidden = false;
        } else { el.aromaWrap.hidden = true; }
      }

      // Note (sample/outlier products)
      if (el.note) {
        if (state.isSample && data.note) {
          el.note.innerHTML = '<svg class="le-qa-note__ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg><span>' + data.note + '</span>';
          el.note.hidden = false;
        } else { el.note.hidden = true; }
      }

      // Selectors: sample products → searchable dropdown; others → chips.
      // Chips split by role so the order matches the PDP: plant source renders
      // ABOVE the size cards, every other choice (terp kit, …) BELOW them.
      if (state.isSample) {
        renderGroups([], el.plant);
        renderGroups([], el.groups);
        if (el.plant) el.plant.hidden = true;
        if (el.groups) el.groups.hidden = true;
        if (el.sampleWrap) el.sampleWrap.hidden = false;
        if (el.sampleLabel) el.sampleLabel.textContent = data.sampleOption || '';
        if (el.sampleMount && window.LE_sampleSelect) {
          state.sampleApi = window.LE_sampleSelect(el.sampleMount, {
            samples: data.samples || [],
            placeholder: 'Select a sample',
            onChange: function (v) {
              state.sel[data.sampleOption] = v;
              ensureAvailableSize();
              renderSizes();
              updateATC();
            }
          });
        }
      } else {
        var allGroups = data.chipGroups || [];
        var plantGroups = [], choiceGroups = [];
        allGroups.forEach(function (g) { (g.plant ? plantGroups : choiceGroups).push(g); });
        renderGroups(plantGroups, el.plant);
        renderGroups(choiceGroups, el.groups);
        if (el.plant) el.plant.hidden = plantGroups.length === 0;
        if (el.groups) el.groups.hidden = choiceGroups.length === 0;
        if (el.sampleWrap) el.sampleWrap.hidden = true;
      }

      // Reset qty + size area to a loading state, then fetch variants.
      setQty(1);
      if (el.sizes) el.sizes.innerHTML = '<div class="le-qa-loading">Loading…</div>';
      if (el.sizeSection) el.sizeSection.hidden = false;
      if (el.atc) el.atc.disabled = true;
      if (el.atcPrice) el.atcPrice.textContent = '';

      sheet.classList.add('le-qa-sheet--open');
      if (scrim) scrim.classList.add('le-qa-scrim--visible');
      if (window.LESheetLock) LESheetLock.lock();

      fetchProduct(data.handle).then(function (product) {
        if (!state || state.data.handle !== data.handle) return; // sheet changed/closed
        state.product = product;
        seedSelection();
        syncGroupActive();
        /* Land on the seeded source's branding. seedSelection() honours
           plantDefault, so opening a card from a CBS collection seeds CBS —
           without this the chip said CBS while the tag, title and bottle still
           said CDT. */
        paintCategory(selectedPlantCat());
        if (state.isSample && state.sampleApi && data.sampleOption) {
          state.sampleApi.setValue(state.sel[data.sampleOption]);
        }
        renderSizes();
        updateATC();
      }).catch(function () {
        if (el.sizes) el.sizes.innerHTML = '<div class="le-qa-loading">Couldn’t load options. <a href="' + (data.url || '#') + '">Open product page →</a></div>';
      });
    }

    function closeSheet() {
      sheet.classList.remove('le-qa-sheet--open');
      if (scrim) scrim.classList.remove('le-qa-scrim--visible');
      if (window.LESheetLock) LESheetLock.unlock();
    }

    /* ---------- chip groups ---------- */
    function renderGroups(groups, target) {
      if (!target) return;
      target.innerHTML = '';
      groups.forEach(function (g) {
        var wrap = document.createElement('div');
        wrap.className = 'le-qa-group';
        var label = document.createElement('div');
        label.className = 'le-qa-group__label';
        label.textContent = g.name;
        wrap.appendChild(label);
        var chips = document.createElement('div');
        chips.className = 'le-qa-chips';
        (g.values || []).forEach(function (val) {
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'le-qa-chip' + (g.plant ? ' le-qa-chip--plant' : '');
          chip.dataset.optionName = g.name;
          chip.dataset.value = val.v;
          if (val.color) chip.style.setProperty('--ps-color', val.color);
          if (val.bg) chip.style.setProperty('--ps-bg', val.bg);
          chip.innerHTML = (val.icon || '') + '<span>' + val.v + '</span>';
          chip.addEventListener('click', function () {
            if (!state) return;
            state.sel[g.name] = val.v;
            ensureAvailableSize();
            syncGroupActive();
            if (g.plant) paintCategory(val.cat);
            renderSizes();
            updateATC();
          });
          chips.appendChild(chip);
        });
        wrap.appendChild(chips);
        if (chips.children.length) target.appendChild(wrap);
      });
    }

    function syncGroupActive() {
      if (!state) return;
      // Chips live in two containers (plant above size, choice below) — sweep both.
      sheet.querySelectorAll('.le-qa-chip').forEach(function (chip) {
        var on = state.sel[chip.dataset.optionName] === chip.dataset.value;
        chip.dataset.active = on ? 'true' : 'false';
      });
    }

    /* ---------- variant resolution ---------- */
    function resolveVariant(sel) {
      if (!state || !state.product) return null;
      return state.product.variants.find(function (v) {
        return state.product.optionNames.every(function (n) { return v.opts[n] === sel[n]; });
      }) || null;
    }

    // Seed every option from the first available variant (so defaults are valid).
    function seedSelection() {
      if (!state || !state.product) return;
      var v = state.product.variants.find(function (x) { return x.available; }) || state.product.variants[0];
      state.sel = {};
      if (v) state.product.optionNames.forEach(function (n) { state.sel[n] = v.opts[n]; });

      // On a category collection (all-CBS, all-BDT, ...) the payload carries
      // plantDefault, so the sheet opens already showing THAT source with the
      // other two beside it. Without this every card defaulted to whatever the
      // first available variant happened to be — always Cannabis Derived.
      // Only applied when the value really exists as a Plant Source option AND
      // some variant carries it, so an unavailable combination is never seeded.
      var want = state.plantDefault;
      if (!want) return;
      var plantName = state.product.optionNames.find(function (n) {
        return /plant source/i.test(n) && !/profile/i.test(n);
      });
      if (!plantName || state.sel[plantName] === want) return;
      var exists = state.product.variants.some(function (x) { return x.opts[plantName] === want; });
      if (!exists) return;
      state.sel[plantName] = want;
      // The seeded size may not exist for this source; ensureAvailableSize()
      // runs right after and hops to one that does.
    }

    // After a chip change, if the current size has no available variant, hop to
    // the first size that does — keeps the Add button live.
    function ensureAvailableSize() {
      if (!state || !state.product || !state.sizeOption) return;
      var cur = resolveVariant(state.sel);
      if (cur && cur.available) return;
      var sizeVals = sizeValues();
      for (var i = 0; i < sizeVals.length; i++) {
        var probe = Object.assign({}, state.sel);
        probe[state.sizeOption] = sizeVals[i];
        var v = resolveVariant(probe);
        if (v && v.available) { state.sel[state.sizeOption] = sizeVals[i]; return; }
      }
    }

    function sizeValues() {
      if (!state || !state.product || !state.sizeOption) return [];
      var seen = [], out = [];
      state.product.variants.forEach(function (v) {
        var val = v.opts[state.sizeOption];
        if (val != null && seen.indexOf(val) === -1) { seen.push(val); out.push(val); }
      });
      return out;
    }

    /* ---------- size cards ---------- */
    function renderSizes() {
      if (!el.sizes || !state || !state.product) return;
      if (!state.sizeOption) {            // no size dimension → hide the section
        if (el.sizeSection) el.sizeSection.hidden = true;
        el.sizes.innerHTML = '';
        return;
      }
      if (el.sizeSection) el.sizeSection.hidden = false;
      if (el.sizeLabel) el.sizeLabel.textContent = 'Choose your ' + state.sizeOption.toLowerCase();
      el.sizes.innerHTML = '';
      sizeValues().forEach(function (sizeVal) {
        var probe = Object.assign({}, state.sel);
        probe[state.sizeOption] = sizeVal;
        var v = resolveVariant(probe);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'le-qa-size';
        btn.dataset.value = sizeVal;
        var unavailable = !v || !v.available;
        if (unavailable) btn.classList.add('le-qa-size--oos');
        if (state.sel[state.sizeOption] === sizeVal) btn.classList.add('le-qa-size--active');
        // Slide-up always shows the per-size price (matches the regular product
        // slide-ups). The PDP keeps sample sizes label-only — that's separate.
        var priceHtml = '';
        if (v) {
          if (v.compareAt > v.price) {
            // Stack compare-at over the sale price so long discounted prices
            // (e.g. 1000ml) don't overflow the button width.
            priceHtml = '<s>' + formatMoney(v.compareAt) + '</s>' +
              '<span class="le-qa-size__now">' + formatMoney(v.price) + '</span>';
          } else {
            priceHtml = '<span class="le-qa-size__now">' + formatMoney(v.price) + '</span>';
          }
        } else {
          priceHtml = '<span class="le-qa-size__now">—</span>';
        }
        btn.innerHTML = '<span class="le-qa-size__label">' + sizeVal + '</span>' +
          '<span class="le-qa-size__price">' + priceHtml + '</span>';
        btn.addEventListener('click', function () {
          if (unavailable) return;
          state.sel[state.sizeOption] = sizeVal;
          el.sizes.querySelectorAll('.le-qa-size').forEach(function (b) { b.classList.remove('le-qa-size--active'); });
          btn.classList.add('le-qa-size--active');
          updateATC();
        });
        el.sizes.appendChild(btn);
      });
    }

    /* ---------- quantity ---------- */
    function setQty(n) {
      if (!state) return;
      state.qty = Math.max(1, n);
      if (el.qtyVal) el.qtyVal.textContent = state.qty;
      updateATC();
    }

    /* ---------- add button (live total) ---------- */
    function updateATC() {
      if (!el.atc || !state) return;
      var v = resolveVariant(state.sel);
      if (v && v.available) {
        el.atc.disabled = false;
        el.atc.dataset.variantId = v.id;
        if (el.atcPrice) el.atcPrice.textContent = '· ' + formatMoney(v.price * state.qty);
      } else {
        el.atc.disabled = true;
        el.atc.removeAttribute('data-variant-id');
        if (el.atcPrice) el.atcPrice.textContent = v ? '· Sold out' : '';
      }
    }

    /* ---------- wiring ---------- */
    if (el.qtyMinus) el.qtyMinus.addEventListener('click', function () { if (state) setQty(state.qty - 1); });
    if (el.qtyPlus) el.qtyPlus.addEventListener('click', function () { if (state) setQty(state.qty + 1); });

    if (el.atc) {
      el.atc.addEventListener('click', function () {
        var variantId = el.atc.dataset.variantId;
        if (!variantId || !state) return;
        el.atc.disabled = true;
        el.atc.setAttribute('data-loading', 'true');
        addToCart(variantId, state.qty)
          .then(function () { closeSheet(); })
          .catch(function (err) { console.error('Quick-add error', err); })
          .finally(function () { el.atc.disabled = false; el.atc.removeAttribute('data-loading'); });
      });
    }

    /* DELEGATED, not bound to the node. The sheet is a singleton in
       theme.liquid so the node should never be replaced — but a listener bound
       to one element is one re-render away from silently doing nothing, and
       this is the control a shopper needs to escape a modal. Delegation costs
       nothing and cannot go stale. */
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-qa-close]')) closeSheet();
    });
    if (scrim) scrim.addEventListener('click', closeSheet);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.classList.contains('le-qa-sheet--open')) closeSheet();
    });

    // Delegated trigger + cache warm-up — works after grid re-renders.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-add]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      var data;
      try { data = JSON.parse(trigger.dataset.qa || '{}'); }
      catch (err) { data = null; }
      if (data && data.handle) openSheet(data);
      else if (data && data.url) window.location.href = data.url;
    });
    document.addEventListener('pointerover', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-quick-add]');
      if (!trigger) return;
      var h = trigger.dataset.qa;
      if (!h) return;
      try { var d = JSON.parse(h); if (d.handle) fetchProduct(d.handle); } catch (err) {}
    }, { passive: true });

    window.LE_openQuickAdd = openSheet;
    window.LE_closeQuickAdd = closeSheet;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
